import React, { useState } from 'react'
import CompanySearch from './company-search'
import api from './api-client'

type State = {
  visible: boolean
  selectedCompany: any | null
  startDate: string
  endDate: string
  loading: boolean
  resultTotal: number | null
  resultBreakdown: { date: string, count: number }[]
  error: string | null
}

const formatYMD = (d: Date): string => {
  // format as YYYY-MM-DD using UTC to avoid tz shifts
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate())).toISOString().slice(0, 10)
}

const getLastMonthRange = (): { start: string, end: string } => {
  const now = new Date()
  // First day of last month (UTC)
  const firstOfLastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))
  // Last day of last month = day 0 of current month
  const lastOfLastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0))
  return { start: formatYMD(firstOfLastMonth), end: formatYMD(lastOfLastMonth) }
}

const createInitialState = (): State => {
  const { start, end } = getLastMonthRange()
  return {
    visible: false,
    selectedCompany: null,
    startDate: start,
    endDate: end,
    loading: false,
    resultTotal: null,
    resultBreakdown: [],
    error: null
  }
}

const TestImpressionReport: React.FC = () => {
  const [state, setState] = useState<State>(createInitialState())

  const openModal = () => {
    setState({ ...state, visible: true })
  }

  const closeModal = () => {
    setState(createInitialState())
  }

  const submit = async () => {
    if (!state.selectedCompany) return
    if (!state.startDate || !state.endDate) {
      setState({ ...state, error: 'Please select a start and end date.' })
      return
    }
    setState({ ...state, loading: true, error: null, resultTotal: null, resultBreakdown: [] })
    try {
      const res = await api.getCompanyImpressions({
        companyId: state.selectedCompany._id,
        from_date: state.startDate,
        to_date: state.endDate
      })
      setState(prev => ({ ...prev, loading: false, resultTotal: res.total, resultBreakdown: res.breakdown }))
    } catch (ex) {
      setState({ ...state, loading: false, error: ex?.message || 'Error fetching impressions' })
    }
  }

  return <>
    <button
      type="button"
      className="btn btn-danger"
      onClick={openModal}
    >
      Test Impression Report For A Company
    </button>
    <div
      className='central-modal'
      style={{display: state.visible ? 'flex' : 'none'}}
      onPointerDown={closeModal}
    >
      <div
        className='modal-content'
        onPointerDown={(e) => e.stopPropagation()}
        onScroll={(e) => e.stopPropagation()}
      >
        <h1>Test Impression Report</h1>
        <div>
          <p>Select a company to test the impression report. For now, this will simply log the company's info.</p>
          <div style={{ marginTop: 10 }}>
            <CompanySearch
              onSelect={(id, co) => setState({ ...state, selectedCompany: co })}
              resultsStyle="dropdown-only"
              buttonText="Select"
              clearAfterSelect={true}
              placeholder="Search companies..."
            />
          </div>
          <div style={{ display: 'flex', gap: '1em', marginTop: 16 }}>
            <div className='form-group-input'>
              <label>Company</label>
              <p>{state.selectedCompany?.name}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '1em', marginTop: 16 }}>
            <div className='form-group-input'>
              <label>Start Date</label>
              <input
                type="date"
                value={state.startDate}
                onChange={e => setState({ ...state, startDate: e.target.value })}
              />
            </div>
            <div className='form-group-input'>
              <label>End Date</label>
              <input
                type="date"
                value={state.endDate}
                onChange={e => setState({ ...state, endDate: e.target.value })}
              />
            </div>
          </div>
          {state.error ? <div className='alert alert-danger' style={{ marginTop: 12 }}>{state.error}</div> : null}
          {state.resultTotal !== null ? (
            <div className='alert alert-secondary' style={{ marginTop: 12 }}>
              <div>Total impressions: <b>{state.resultTotal}</b></div>
              {state.resultBreakdown?.length ? (
                <div style={{ maxHeight: 140, overflowY: 'auto', marginTop: 8 }}>
                  {state.resultBreakdown.map(d => (
                    <div key={d.date} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>{d.date}</span>
                      <span>{d.count}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div style={{display: 'flex', flexDirection: 'row', gap: '5em', margin: '2em 0 0 0'}}>
          <button className='btn btn-secondary' onClick={closeModal}>Close</button>
          <button
            className='btn btn-primary'
            onClick={submit}
            disabled={!state.selectedCompany || state.loading}
          >
            {state.loading ? 'Loading…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  </>
}

export default TestImpressionReport


